import React, { FunctionComponent, useRef, useEffect, useState, useMemo, ChangeEvent } from 'react';
import styles from 'src/layouts/common/TOTPAuthentication/TOTPAuthentication.css';
import cn from 'classnames';
import Button, { ButtonSize, ButtonType } from 'src/components/Button';
import { useIntl } from 'react-intl';
import ErrorMessage from 'src/components/ErrorMessage';
import { connect, useDispatch } from 'react-redux';
import actions from 'src/app/redux/store/actions';
import selectors from 'src/app/redux/selectors';
import LoadingOverlay from 'src/components/LoadingOverlay';
import Timer from 'src/components/Timer/Timer';
import { IAppState } from 'src/app/redux/store/reducers';
import { ERROR_CODE_NOT_MATCH, ERROR_CODE_TIME_EXPIRED, TOTP_NUMBER_OF_DIGIT, getTime, hideEmail, isValidationsError, setTOTPAuthValue } from 'src/layouts/common/TOTPAuthentication/TOTPAuthenticationUtils';
import errorsActions from 'src/app/redux/modules/errors/actions';
import { TLocaleId } from 'src/i18n';

interface ITOTPAuthenticationProps {
    validateTOTP: (TOTP: string) => Promise<boolean>;
    resendTOTP: () => Promise<void>;
    userTimeZoneId: string;
    onVerificationOTP?: (isTOTPVerificationRequired: boolean) => void;
    logOut: () => void;
    emailId: string;
}

interface IControlledBtnProps {
    btnText: string;
    className: string;
    disabled: boolean;
    btnSize?: ButtonSize;
    btnType?: ButtonType;
    onChange: () => void;
}

const TOTPAuthentication: FunctionComponent<ITOTPAuthenticationProps> = (props: ITOTPAuthenticationProps) => {

    const {
        validateTOTP,
        resendTOTP,
        userTimeZoneId,
        onVerificationOTP,
        logOut,
        emailId,
    } = props;

    const intl = useIntl();
    const dispatch = useDispatch();

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    const [totp, setTotp] = useState<string[]>(new Array(TOTP_NUMBER_OF_DIGIT).fill(''));
    const [isTimerComplete, setTimerComplete] = useState<boolean>(false);
    const [timeToExpire, setTimeToExpire] = useState<number>(getTime(userTimeZoneId));
    const [isOtpIncomplete, setIsOtpIncomplete] = useState(true);

    const hiddenEmail = hideEmail(emailId);
    const totpBoxReference = useRef<HTMLInputElement[]>([]);

    const setErrorMessage = (text: TLocaleId) => {
        const errorMsg = intl.formatMessage({ id: text });
        setError(errorMsg);
        dispatch(errorsActions.clearError());
    };

    const focusOnFirstInput = () => {
      totpBoxReference.current[0].focus();
    };

    useEffect(() => {
      focusOnFirstInput();
    }, []);

    function onTimerComplete() {
        setTimerComplete(true);
    }

    function renderTimer() {
        return (
            <Timer expiresInTime={timeToExpire} onCompletion={onTimerComplete} className={styles.countdown} />
        );
    }

    const timer = useMemo(() => {
        return renderTimer();
    }, [userTimeZoneId, timeToExpire]);

    const handleResendTOTPBtn = async () => {

        try {
            setLoading(true);
            setError('');
            await resendTOTP();
            setTimerComplete(false);
            setTimeToExpire(getTime(userTimeZoneId));
        }
        catch (e) {
            setError(e.code.message);
        }
        finally {
            focusOnFirstInput();
            setLoading(false);
        }
    };

    const handleOnLoginBtn = async () => {
      try {
        setLoading(true);
        setError('');
        const TOTP = totp.join('');
        const isTOTPValid = await validateTOTP(TOTP);
        if (isTOTPValid) {
          setTOTPAuthValue(false);
          if (onVerificationOTP) {
            onVerificationOTP(false);
          }
        }
      } catch (e) {
        setTotp(new Array(TOTP_NUMBER_OF_DIGIT).fill(''));
        setIsOtpIncomplete(true);
        focusOnFirstInput();
        if (isValidationsError(e, ERROR_CODE_NOT_MATCH)) {
          setErrorMessage('MursionPortal.TOTP.IncorrectCode.ErrorMsg');
        }
        if (isValidationsError(e, ERROR_CODE_TIME_EXPIRED)) {
          setErrorMessage('MursionPortal.TOTP.ResendNewCode.ErrorMsg');
        }
      } finally {
        setLoading(false);
      }
    };

    const handleOnCancelBtn = () => {
        logOut();
    };

    const getControlledBtn = (controlledBtnProps: IControlledBtnProps) => {

        const { btnText, className, disabled, btnSize, btnType, onChange } = controlledBtnProps;

        return (
            <Button
                className={cn(className, 'mb-2 mx-1')}
                aria-label={btnText}
                disabled={disabled}
                btnSize={btnSize}
                btnType={btnType}
                onClick={onChange}
            >
                {btnText}
            </Button>
        );
    };

    const getOtpInputFields = () => {

        const handleChange = (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
            const newArr: string[] = [...totp];
            const value = e.currentTarget.value;
            newArr[index] = value;
            const everyBoxFilled = newArr.every((digit) => digit !== '');
            setTotp(newArr);
            setIsOtpIncomplete(!everyBoxFilled);

            if (value && index < TOTP_NUMBER_OF_DIGIT - 1) {
                totpBoxReference.current[index + 1].focus();
            }
        };

        const handleBackspaceAndEnter = (index: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Backspace' && !e.currentTarget.value && index > 0) {
                totpBoxReference.current[index - 1].focus();
            }

            if (e.key === 'Enter' && e.currentTarget.value && index < TOTP_NUMBER_OF_DIGIT - 1) {
                totpBoxReference.current[index + 1].focus();
            }
        };

        const setRef = (index: number) => (reference: HTMLInputElement | null) => {
            if (reference) {
                totpBoxReference.current[index] = reference;
            }
        };

        return (
            <div className={styles.authfields}>
                {totp.map((digit, index) => (
                    <input
                        key={index}
                        value={digit}
                        maxLength={1}
                        onChange={handleChange(index)}
                        onKeyUp={handleBackspaceAndEnter(index)}
                        ref={setRef(index)}
                        className={styles.otpInputText}
                    />
                ))}
            </div>
        );
    };

    return (
      <LoadingOverlay active={loading} spinner={true}>
        <div className={cn(styles.psAuthenticationWrap)}>
          <div className={styles.authText}>
            {intl.formatMessage({ id: 'MursionPortal.TOTP.Page.Heading' }, { email: hiddenEmail })}
          </div>
          <div className={styles.authInput}>
            <label>{intl.formatMessage({ id: 'MursionPortal.TOTP.Code.Label' })}</label>
            {getOtpInputFields()}
            <div className={styles.autfooter}>
              <time>{isTimerComplete ? null : timer}</time>
              {getControlledBtn({
                btnText: intl.formatMessage({ id: 'MursionPortal.TOTP.ResendOTP.Text' }),
                className: styles.resendBtn,
                disabled: !isTimerComplete,
                onChange: handleResendTOTPBtn,
              })}
            </div>
            <ErrorMessage className={styles.error} message={error} />
          </div>
          <div className={styles.authBtnWrap}>
            {getControlledBtn({
              btnText: intl.formatMessage({ id: 'Login.Login' }),
              className: styles.submitBtn,
              disabled: isOtpIncomplete,
              btnSize: ButtonSize.MEDIUM,
              btnType: ButtonType.LIGHT_BLUE,
              onChange: handleOnLoginBtn,
            })}
            {getControlledBtn({
              btnText: intl.formatMessage({ id: 'Session.Cancel' }),
              className: styles.submitBtn,
              disabled: false,
              btnSize: ButtonSize.MEDIUM,
              btnType: ButtonType.LIGHT_BLUE,
              onChange: handleOnCancelBtn,
            })}
          </div>
        </div>
      </LoadingOverlay>
    );
};

export default connect((state: IAppState) => ({
    userTimeZoneId: selectors.profile.getUserTimeZoneId(state),
    emailId: selectors.profile.getUserEmail(state),
}), {
    resendTOTP: actions.profile.resendTOTP,
    validateTOTP: actions.profile.validateTOTP,
    logOut: actions.auth.logOut,
})(TOTPAuthentication);